import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faLayerGroup } from '@fortawesome/pro-regular-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { showWarningThunk } from '@spike/notifications-action';
import Spinner from 'components/UI/V3/Spinner';
import { RootState } from 'store';
import { PetsState } from '@spike/pets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { getCapacityInfo } from 'components/CreateBooking/Daycare/Utils/GetCapacityInfo';
import { getWeightRangeInfo } from 'components/CreateBooking/Daycare/Utils/GetWeightRangeInfo';
import AlertMessage from 'components/UI/V3/AlertMessage';
import UnitSelector, { UnitBadge } from '../UnitSelector/UnitSelector';
import { BoardingService } from 'model/Service';
import { Room, RoomRule, RoomRuleTypeIds, RoomUnit } from '@spike/room-model';
import styles from '../StandardRateSelector/StandardRateSelector.module.scss';

export interface BoardingUnitSelectorSectionProps {
    service: BoardingService;
    room?: Room;
    petId: number;
    roomUnitId?: number;
    onSelectRoomUnitId: (roomUnitId: number | undefined) => void;
    isLoading?: boolean;
}

type CapacityType = 'weight' | 'size' | 'quantity' | 'weightRange' | null;

type CapacityInfo = {
    type: CapacityType;
    max?: number;
};

type UnitIndicator = UnitBadge & {
    remainingCapacity: number;
    includedInRoomSummary: boolean;
};

type RoomIndicator = {
    text: string;
    showWarningIcon: boolean;
};

const SIZE_MISMATCH_WARNING_TEXT = 'This room does not allow pets of this size.';

const isBySizeInvalidUnit = (unit: RoomUnit): boolean => {
    const eligibility = unit.sizeEligibility;
    return eligibility === 'mismatch' || eligibility === 'inconsistent';
};

const isBySizeRoom = (units: Array<RoomUnit>): boolean =>
    units.some(unit => unit.sizeEligibility !== null && unit.sizeEligibility !== undefined);

const getBySizeRuleData = (rule?: RoomRule): Array<{ petTypeId?: string; sizeId?: string; quantity?: number }> => {
    if (!rule?.data) return [];

    try {
        const parsed = JSON.parse(rule.data);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getBySizeCapacity = (room: Room, unit: RoomUnit, pet?: PetsState['list'][number]): number | undefined => {
    const petTypeId = pet?.type?.plural?.toLowerCase();
    const petSizeId = pet?.size?.id;

    if (!petTypeId || !petSizeId) return undefined;

    const matchesPet = (ruleData: { petTypeId?: string; sizeId?: string; quantity?: number }): boolean =>
        String(ruleData.petTypeId) === String(petTypeId) && String(ruleData.sizeId) === String(petSizeId);

    const activeRule = room.rules.find(
        rule => rule.ruleTypeId === RoomRuleTypeIds.petSizeRuleId && String(rule.id) === String(unit.activeSizeRuleId)
    );

    const activeRuleQuantity = getBySizeRuleData(activeRule).find(matchesPet)?.quantity;

    if (activeRuleQuantity != null) {
        return activeRuleQuantity;
    }

    const fallbackRule = room.rules.find(
        rule => rule.ruleTypeId === RoomRuleTypeIds.petSizeRuleId && getBySizeRuleData(rule).some(matchesPet)
    );

    return getBySizeRuleData(fallbackRule).find(matchesPet)?.quantity;
};

export const BoardingUnitSelectorSection: FunctionComponent<BoardingUnitSelectorSectionProps> = ({
    service,
    room,
    petId,
    roomUnitId,
    onSelectRoomUnitId,
    isLoading
}) => {
    const dispatch = useDispatch();

    const petsState = useSelector<RootState, PetsState>(state => state.pets);
    const pet = petsState.list.find(p => p.id === petId);

    const [selectedRoomUnit, setSelectedRoomUnit] = useState<RoomUnit | undefined>(undefined);

    const currentRoom: Room | undefined = room || service.rooms?.[0];
    const normalizedUnits = currentRoom ? currentRoom.units : [];
    const hasBySize = isBySizeRoom(normalizedUnits);

    const capacityInfo = useMemo<CapacityInfo>(() => getCapacityInfo(currentRoom), [currentRoom]);

    useEffect(() => {
        if (!currentRoom || !selectedRoomUnit) return;

        const updated = normalizedUnits.find(u => u.id === selectedRoomUnit.id);
        if (updated) {
            setSelectedRoomUnit(updated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRoom, normalizedUnits]);

    useEffect(() => {
        if (!currentRoom || normalizedUnits.length === 0) return;

        if (roomUnitId == null) {
            return;
        }

        const preUnit = normalizedUnits.find(u => u.id === roomUnitId);
        setSelectedRoomUnit(preUnit);
    }, [currentRoom, roomUnitId, normalizedUnits]);

    const prevServiceId = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (prevServiceId.current && prevServiceId.current !== service.serviceId) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
        }

        prevServiceId.current = service.serviceId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service.serviceId]);

    useNonInitialEffect(() => {
        if (!currentRoom || currentRoom.rules[0]?.ruleTypeId !== 4) return;

        const range = getWeightRangeInfo(currentRoom);
        if (!range) return;

        const petWeight = pet?.weight;
        if (!petWeight || petWeight <= 0) return;

        const outOfRange =
            (range.min !== undefined && petWeight < range.min) || (range.max !== undefined && petWeight > range.max);

        if (outOfRange) {
            dispatch(showWarningThunk('This room does not allow pets of this weight.'));
        }
    }, [currentRoom, dispatch, pet?.weight]);

    const getRuleWarningType = (roomToEvaluate?: Room): 'weight' | null => {
        if (!roomToEvaluate) return null;

        if (capacityInfo.type === 'weight' || capacityInfo.type === 'weightRange') {
            const petWeight = pet?.weight;

            if (!petWeight || petWeight <= 0) {
                return 'weight';
            }

            if (capacityInfo.type === 'weightRange') {
                const range = getWeightRangeInfo(roomToEvaluate);
                if (
                    range &&
                    ((range.min != null && petWeight < range.min) || (range.max != null && petWeight > range.max))
                ) {
                    return 'weight';
                }
            }
        }

        return null;
    };

    const weightRuleWarning = useMemo(
        () => getRuleWarningType(currentRoom),
        [currentRoom, capacityInfo.type, pet?.weight]
    );

    const unitIndicators = useMemo(() => {
        const indicators = new Map<number, UnitIndicator>();

        normalizedUnits.forEach(unit => {
            if (unit.id == null) {
                return;
            }

            if (unit.active === false) {
                indicators.set(unit.id, {
                    text: 'Unavailable',
                    showWarningIcon: true,
                    remainingCapacity: 0,
                    includedInRoomSummary: false
                });
                return;
            }

            if (weightRuleWarning === 'weight') {
                indicators.set(unit.id, {
                    text: 'By Weight',
                    showWarningIcon: true,
                    remainingCapacity: 0,
                    includedInRoomSummary: false
                });
                return;
            }

            if (hasBySize) {
                if (!currentRoom || isBySizeInvalidUnit(unit)) {
                    indicators.set(unit.id, {
                        text: 'By Size',
                        showWarningIcon: true,
                        remainingCapacity: 0,
                        includedInRoomSummary: false
                    });
                    return;
                }

                const max = getBySizeCapacity(currentRoom, unit, pet);
                const occupied = unit.occupancy ?? 0;
                const rawRemaining = max != null ? max - occupied : unit.available ? 1 : 0;
                const remainingCapacity = unit.available ? rawRemaining : Math.min(rawRemaining, 0);

                indicators.set(unit.id, {
                    text: max != null ? `${remainingCapacity}/${max}` : unit.available ? 'Available' : 'Unavailable',
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            const occupied = unit.occupancy ?? 0;

            if (capacityInfo.type === 'weight') {
                const petWeight = pet?.weight ?? 0;
                const max = capacityInfo.max ?? 0;

                if (!petWeight || petWeight <= 0 || !max) {
                    indicators.set(unit.id, {
                        text: 'By Weight',
                        showWarningIcon: true,
                        remainingCapacity: 0,
                        includedInRoomSummary: false
                    });
                    return;
                }

                const rawRemainingWeight = max - occupied;
                const remainingWeight = unit.available === false ? Math.min(rawRemainingWeight, 0) : rawRemainingWeight;
                const remainingCapacity = Math.floor(remainingWeight / petWeight);

                indicators.set(unit.id, {
                    text: `${remainingWeight}/${max} lbs`,
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            if (capacityInfo.max != null) {
                const rawRemainingCapacity = capacityInfo.max - occupied;
                const remainingCapacity =
                    unit.available === false ? Math.min(rawRemainingCapacity, 0) : rawRemainingCapacity;

                indicators.set(unit.id, {
                    text: `${remainingCapacity}/${capacityInfo.max}`,
                    showWarningIcon: remainingCapacity < 1,
                    remainingCapacity,
                    includedInRoomSummary: true
                });
                return;
            }

            const canAccommodate = unit.available !== false;

            indicators.set(unit.id, {
                text: canAccommodate ? 'Available' : 'Unavailable',
                showWarningIcon: !canAccommodate,
                remainingCapacity: canAccommodate ? 1 : 0,
                includedInRoomSummary: true
            });
        });

        return indicators;
    }, [normalizedUnits, weightRuleWarning, hasBySize, currentRoom, pet, capacityInfo]);

    const roomIndicator = useMemo<RoomIndicator | null>(() => {
        if (!currentRoom) {
            return null;
        }

        const enabledUnits = normalizedUnits.filter(unit => unit.active !== false);

        if (enabledUnits.length === 0) {
            return {
                text: 'Occupied · 0/0',
                showWarningIcon: true
            };
        }

        if (weightRuleWarning === 'weight') {
            return {
                text: 'Unavailable — By Weight',
                showWarningIcon: true
            };
        }

        const includedIndicators = enabledUnits
            .map(unit => (unit.id != null ? unitIndicators.get(unit.id) : undefined))
            .filter((indicator): indicator is UnitIndicator => Boolean(indicator?.includedInRoomSummary));

        const totalUnits = includedIndicators.length;

        if (totalUnits === 0 && hasBySize) {
            return {
                text: 'Unavailable — By Size',
                showWarningIcon: true
            };
        }

        if (totalUnits === 0) {
            return {
                text: 'Occupied · 0/0',
                showWarningIcon: true
            };
        }

        const availableUnits = includedIndicators.filter(indicator => indicator.remainingCapacity >= 1).length;
        const hasOverbookedUnits = includedIndicators.some(indicator => indicator.remainingCapacity < 0);

        if (availableUnits > 0) {
            return {
                text: `Available · ${availableUnits}/${totalUnits}`,
                showWarningIcon: hasOverbookedUnits
            };
        }

        return {
            text: `Occupied · 0/${totalUnits}`,
            showWarningIcon: true
        };
    }, [currentRoom, normalizedUnits, weightRuleWarning, unitIndicators, hasBySize]);

    const shouldShowBySizeWarning = useMemo(() => {
        if (!hasBySize) {
            return false;
        }

        if (selectedRoomUnit) {
            return isBySizeInvalidUnit(selectedRoomUnit);
        }

        return roomIndicator?.text === 'Unavailable — By Size';
    }, [hasBySize, selectedRoomUnit, roomIndicator]);

    const handleSelectRoomUnitId = (unitId: number | undefined) => {
        if (unitId == null) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
            return;
        }

        const roomUnit = normalizedUnits.find(unit => unit.id === unitId);

        if (!roomUnit) {
            setSelectedRoomUnit(undefined);
            onSelectRoomUnitId(undefined);
            return;
        }

        if (weightRuleWarning === 'weight') {
            dispatch(showWarningThunk('This room does not allow pets of this weight.'));
        } else if (hasBySize && isBySizeInvalidUnit(roomUnit)) {
            dispatch(showWarningThunk(SIZE_MISMATCH_WARNING_TEXT));
        } else {
            const indicator = roomUnit.id != null ? unitIndicators.get(roomUnit.id) : undefined;

            if (indicator && indicator.remainingCapacity < 1) {
                dispatch(showWarningThunk('This room is already at full capacity for the selected time'));
            }
        }

        setSelectedRoomUnit(roomUnit);
        onSelectRoomUnitId(roomUnit.id);
    };

    const conflictingTags = useMemo(() => {
        if (!currentRoom || !pet) return [];

        const petTags = [...(pet.positivePersonalitiesList ?? []), ...(pet.negativePersonalitiesList ?? [])];

        const roomRestrictedTags = currentRoom.restrictions?.petTagIds ?? [];

        return petTags.filter(tag => roomRestrictedTags.includes(tag));
    }, [currentRoom, pet]);

    const getUnitBadge = (unit: RoomUnit): UnitBadge | undefined => {
        if (unit.id == null) {
            return undefined;
        }

        const indicator = unitIndicators.get(unit.id);

        if (!indicator) {
            return undefined;
        }

        return {
            text: indicator.text,
            showWarningIcon: indicator.showWarningIcon
        };
    };

    return (
        <div className={styles.unitSelectorContainer}>
            {isLoading ? (
                <div className={styles.spinnerContainer}>
                    <Spinner size={32} />
                </div>
            ) : (
                <>
                    <div className={styles.unitSelectorHeader}>
                        <span className={styles.unitSelectorLabel}>{currentRoom?.name}</span>

                        {roomIndicator && (
                            <div className={styles.unitSelectorQty}>
                                <FontAwesomeIcon
                                    icon={roomIndicator.showWarningIcon ? faExclamationTriangle : faLayerGroup}
                                    className={clsx({
                                        [styles.warningIcon]: roomIndicator.showWarningIcon
                                    })}
                                />
                                <span>{roomIndicator.text}</span>
                            </div>
                        )}
                    </div>

                    <UnitSelector
                        initialUnitId={selectedRoomUnit?.id}
                        units={normalizedUnits}
                        onSelect={handleSelectRoomUnitId}
                        getUnitBadge={getUnitBadge}
                    />

                    {shouldShowBySizeWarning && (
                        <AlertMessage
                            className={styles.alertConflictingTags}
                            bWidth="2px"
                            icon={faExclamationTriangle}
                            iconColor="#EAB464"
                            bgColor="#F8F5F1"
                            bdColor="#BCB8AE"
                        >
                            <span className={styles.alertText}>{SIZE_MISMATCH_WARNING_TEXT}</span>
                        </AlertMessage>
                    )}

                    {conflictingTags.length > 0 && (
                        <AlertMessage
                            className={styles.alertConflictingTags}
                            bWidth="2px"
                            icon={faExclamationTriangle}
                            iconColor="#EAB464"
                            bgColor="#F8F5F1"
                            bdColor="#BCB8AE"
                        >
                            <span className={styles.alertText}>
                                This room isn’t available for pets with tags: <b>{conflictingTags.join(', ')}.</b>
                            </span>
                        </AlertMessage>
                    )}
                </>
            )}
        </div>
    );
};

export default BoardingUnitSelectorSection;
